import { ROLE_LABEL } from '@/constants/roles';
import { formatDate, formatDateTime } from '@/lib/format';
import type { StaffMember } from '@/types/staff';
import type { StaffAttendanceSummary } from '@/types/staffAttendance';
import { STAFF_STATUS_LABEL } from '../constants';

/**
 * What goes to paper from a staff profile.
 *
 * Never drawn on screen — `PrintDocument` mounts these only while printing.
 * Plain like the fee receipt: black on white, no cards and no colour that costs
 * a school ink. The institute's name comes from branding, never from this file
 * (CLAUDE.md §4), and only fields the API actually holds are printed — the
 * profile's placeholder payroll and statutory figures stay off the paper.
 */

interface StaffDocumentProps {
  member: StaffMember;
  instituteName: string;
  instituteCity: string | null;
  /** An object URL the caller fetched and will revoke, or null for initials. */
  photoUrl: string | null;
}

function staffTypeLabel(member: StaffMember): string {
  return ROLE_LABEL[member.staffType];
}

function initialsOf(member: StaffMember): string {
  return `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
}

function Photo({ member, photoUrl, className }: { member: StaffMember; photoUrl: string | null; className: string }) {
  return (
    <div className={`flex shrink-0 items-center justify-center overflow-hidden border border-black ${className}`}>
      {photoUrl ? (
        <img src={photoUrl} alt={member.fullName} className="h-full w-full object-cover" />
      ) : (
        <span className="text-xl font-bold">{initialsOf(member)}</span>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <>
      <dt className="py-1 pr-3 align-top font-semibold">{label}</dt>
      <dd className="py-1 pr-6">{value || '—'}</dd>
    </>
  );
}

export interface StaffProfileSheetProps extends StaffDocumentProps {
  /** This month's figures, or null when they could not be read. */
  attendance: StaffAttendanceSummary | null;
  /** `April 2026` — the month `attendance` describes. */
  attendanceMonthLabel: string;
}

/** One member of staff's record, for the file or for "Save as PDF". */
export function StaffProfileSheet({
  member,
  instituteName,
  instituteCity,
  photoUrl,
  attendance,
  attendanceMonthLabel,
}: StaffProfileSheetProps) {
  return (
    <article className="bg-white p-6 text-black">
      <header className="border-b-2 border-black pb-3 text-center">
        <h1 className="text-xl font-bold">{instituteName}</h1>
        {instituteCity && <p className="text-sm">{instituteCity}</p>}
        <p className="mt-2 text-sm font-semibold uppercase tracking-widest">Staff profile</p>
      </header>

      <div className="mt-5 flex items-start gap-5">
        <Photo member={member} photoUrl={photoUrl} className="h-28 w-24" />
        <div className="min-w-0">
          <h2 className="text-lg font-bold">{member.fullName}</h2>
          <p className="text-sm">
            {[member.designation, member.role?.name ?? staffTypeLabel(member)].filter(Boolean).join(' · ')}
          </p>
          <p className="mt-1 text-sm">
            <span className="font-semibold">Status</span> {STAFF_STATUS_LABEL[member.status]}
          </p>
        </div>
      </div>

      <h3 className="mt-6 border-b border-black pb-1 text-sm font-bold uppercase tracking-widest">
        Account
      </h3>
      <dl className="mt-2 grid grid-cols-[auto_1fr_auto_1fr] text-sm">
        <Detail label="Employee code" value={member.employeeCode} />
        <Detail label="Account type" value={staffTypeLabel(member)} />
        <Detail label="Role" value={member.role?.name ?? null} />
        <Detail label="Designation" value={member.designation} />
        <Detail label="Joined on" value={member.joinedOn ? formatDate(member.joinedOn) : null} />
        <Detail label="Last sign-in" value={member.lastLoginAt ? formatDateTime(member.lastLoginAt) : 'Never'} />
      </dl>

      <h3 className="mt-6 border-b border-black pb-1 text-sm font-bold uppercase tracking-widest">
        Contact
      </h3>
      <dl className="mt-2 grid grid-cols-[auto_1fr_auto_1fr] text-sm">
        <Detail label="Email" value={member.email} />
        <Detail label="Mobile" value={member.mobile} />
      </dl>

      {member.subjects && member.subjects.length > 0 && (
        <>
          <h3 className="mt-6 border-b border-black pb-1 text-sm font-bold uppercase tracking-widest">
            Subjects taught
          </h3>
          <p className="mt-2 text-sm">{member.subjects.join(', ')}</p>
        </>
      )}

      <h3 className="mt-6 border-b border-black pb-1 text-sm font-bold uppercase tracking-widest">
        Attendance — {attendanceMonthLabel}
      </h3>
      {attendance ? (
        <dl className="mt-2 grid grid-cols-[auto_1fr_auto_1fr] text-sm">
          <Detail label="Working days" value={String(attendance.workingDaysSoFar)} />
          <Detail label="Present" value={String(attendance.present)} />
          <Detail label="On leave" value={String(attendance.leave)} />
          <Detail label="Absent" value={String(attendance.absent)} />
          <Detail label="Late arrivals" value={String(attendance.late)} />
          <Detail
            label="Attendance rate"
            value={attendance.attendanceRate === null ? null : `${attendance.attendanceRate}%`}
          />
        </dl>
      ) : (
        <p className="mt-2 text-sm">The attendance for this month could not be read.</p>
      )}

      <p className="mt-10 text-center text-xs">
        Computer generated on {formatDateTime(new Date())}. Confidential — for the school office only.
      </p>
    </article>
  );
}

/**
 * A staff identity card at the standard card size (85.6 × 54 mm), so it can be
 * cut out and laminated, or sent to a card printer as-is.
 */
export function StaffIdCard({ member, instituteName, instituteCity, photoUrl }: StaffDocumentProps) {
  return (
    <article className="h-[54mm] w-[85.6mm] overflow-hidden border border-black bg-white text-black">
      <header className="border-b border-black px-3 py-1.5 text-center">
        <p className="truncate text-xs font-bold uppercase">{instituteName}</p>
        {instituteCity && <p className="truncate text-[9px]">{instituteCity}</p>}
      </header>

      <div className="flex gap-3 px-3 py-2">
        <Photo member={member} photoUrl={photoUrl} className="h-[27mm] w-[22mm]" />
        <dl className="min-w-0 text-[10px] leading-snug">
          <dt className="sr-only">Name</dt>
          <dd className="truncate text-xs font-bold">{member.fullName}</dd>
          <dt className="sr-only">Designation</dt>
          <dd className="truncate">{member.designation || member.role?.name || staffTypeLabel(member)}</dd>
          <dt className="mt-1.5 font-semibold">Employee code</dt>
          <dd className="font-mono">{member.employeeCode || '—'}</dd>
          {member.mobile && (
            <>
              <dt className="mt-1 font-semibold">Mobile</dt>
              <dd className="font-mono">{member.mobile}</dd>
            </>
          )}
        </dl>
      </div>

      <p className="border-t border-black px-3 py-1 text-center text-[9px] font-semibold uppercase tracking-widest">
        Staff identity card
      </p>
    </article>
  );
}
